import { all, call, fork, put, spawn } from "redux-saga/effects";

import { getAuthInstance } from "@tailormed/common-client/services/auth";
import { waitForLogin } from "@tailormed/common-client/store/saga/authSagas";
import initializeUserMetrics from "@tailormed/common-client/store/saga/services/userMetrics";
import { ApiNames } from "../../api/api";
import ErrorMessage from "../../component/customComponent/customMessages/ErrorMessage";
import { oktaConfig } from "../../constant/okta";
import ACTION from "../action";
import { getUser, watchSignInActions } from "./authActions";
import { callApi } from "./networkActions";
import { actions, setSummaryCounters } from "./sagaActions";

function* getUsers() {
  const result = yield callApi(ApiNames.Users);
  if (result && result.users) {
    const { users } = result;
    const usersMap = users.reduce((obj, item) => Object.assign(obj, { [item.id]: item }), {});
    yield put(ACTION.setUsers(usersMap));
  } else {
    ErrorMessage(`Failed to get users`);
  }
}

function* getAssignees() {
  const result = yield callApi(ApiNames.Assignees);
  if (result) {
    const { users } = result;
    const assignees = users.reduce((obj, item) => Object.assign(obj, { [item.id]: item }), {});
    yield put(ACTION.setAssignees(assignees));
  } else {
    ErrorMessage(`Failed to get assignees`);
  }
}

function* getUserGroups() {
  const result = yield callApi(ApiNames.UserGroups);
  if (result) {
    const userGroups = result["user-groups"].reduce((obj, item) => Object.assign(obj, { [item.id]: item }), {});
    yield put(ACTION.setUserGroups(userGroups));
  } else {
    ErrorMessage(`Failed to get user groups`);
  }
}

function* getUserGroupsWithUsers() {
  try {
    const result = yield callApi(ApiNames.UserGroupsWithUsers);

    if (result) {
      yield put(ACTION.setUserGroupsWithUsers(result));
    } else {
      ErrorMessage(`Failed to get user groups with users`);
    }
  } catch (error) {
    ErrorMessage(`Error fetching user groups with users: ${error.message}`);
  }
}

function* getSettings() {
  try {
    const settingsRes = yield callApi(ApiNames.Settings);
    if (settingsRes) {
      yield put(ACTION.setSettings(settingsRes.settings));
    }
  } catch (error) {
    ErrorMessage(`Failed to get settings: ${error}`);
  }
}

function* getUnits() {
  try {
    const getUnitsRes = yield callApi(ApiNames.GetUnits);
    if (getUnitsRes) {
      yield put(ACTION.setUnits(getUnitsRes));
    }
  } catch (error) {
    ErrorMessage("Access Denied");
  }
}

function* getVisitTypes() {
  try {
    const getVisitTypesRes = yield callApi(ApiNames.GetVisitTypes);
    if (getVisitTypesRes) {
      yield put(ACTION.setVisitTypes(getVisitTypesRes));
    }
  } catch (error) {
    ErrorMessage("Access Denied");
  }
}

function* getProviders() {
  const providerTypes = ["facility", "doctor"];
  for (let type of providerTypes) {
    const result = yield callApi(ApiNames.Providers, type);
    if (result) {
      yield put(ACTION.provider(type, result.providers));
    } else {
      ErrorMessage(`Failed to get providers data`);
    }
  }
}

function* getAPSponsors() {
  const result = yield callApi(ApiNames.getAPSponsors);
  if (result) {
    yield put(ACTION.APSponsors(result.APSponsors));
  } else {
    ErrorMessage(`Failed to get APSponsors data`);
  }
}

function* startup() {
  const oktaAuth = getAuthInstance(oktaConfig.redirectUri);
  const token = oktaAuth.getAccessToken();
  yield all([call(getUser), call(getSettings)]);
  yield spawn(initializeUserMetrics, { analytics: true, rum: true, token });
  yield spawn(getUsers);
  yield spawn(getAssignees);
  yield spawn(getUserGroups);
  yield spawn(getUserGroupsWithUsers);
  yield spawn(setSummaryCounters);
  yield spawn(getUnits);
  yield spawn(getVisitTypes);
  yield spawn(getProviders);
  yield spawn(getAPSponsors);
}

function* rootSaga() {
  yield fork(watchSignInActions);
  yield waitForLogin();
  yield fork(startup);
  yield spawn(actions);
}

export { rootSaga };
